import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent
} from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Modal,
  DropdownWithSearch,
  MRTDataTable,
  ToastNotification,
  ToastNotificationVariant,
  TabFields,
  CmhcIcon,
  CmhcIconSize,
  SplitPane,
  Tag,
  TagType,
  TagVariant
} from '@cmhc-schl/hearth-ui';
import { Box, Menu, MenuItem, ClickAwayListener, Stack, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import lodash from 'lodash';
import { MRT_Row, MRT_SortingState } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import { ActionDropdownWithListItems } from '../../../../shared/components/ActionDropdownWithListItems';
import { EpipProcessingStatusCode } from '../../../HbtPortfolioManagementPage/types';
import { getLoanInventoryTableData } from '../../api/RequestOverviewService';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import { LoansDropDown } from '../Flyout/component/loansDropDown';
import HbtPortfolioIndividualLoanDetails from '../HbtIndividualLoanDetails';
import RightPaneContent from '../RightPaneContent';
import { useLoansErrors } from '../RightPaneContent/hooks/useLoanErrors';
import { createLoansInventoryColumns } from './columns/loansInventoryTableColumns';
import styles from './styles.module.scss';
import {
  HbtPortfolioLoansInventoryProps,
  LoanInventoryTableFields,
  FilterOption,
  FlyoutType
} from './types';
import { errorOptionsGrouped } from './utils/errorOptionsGrouped';
import { errorSeverityMapping } from './utils/ErrorSeverityMapping';
import { portfolioOptionsGrouped } from './utils/portfolioOptionsGrouped';
import { statusOptionsGrouped } from './utils/statusOptionsGrouped';
import { applyFilters } from './utils/applyFilters';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { useLocation } from 'react-router-dom';
import ForceStatusFlyout from 'Feature/V2/HbtMasterPool/components/HbtMasterPool/component/ForceStatusFlyout';
import { config } from '../../../../../../../config';
import { LoanStatus } from '../HbtIndividualLoanDetails/types';
import HistoricalDataFlyout from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolLoansInventoryTable/components/HistoricalDataFlyout/HistoricalDataFlyout';
import { getRemarksDropDown } from './utils/getRemarksDropDown';
import RichTextBody from 'Feature/V2/Shared_Portfolio/components/RichTextBody/RichTextBody';

const MAX_VISIBLE_FILTERS = 10;
const origin = config.app.internalOriginUrl;
const fetchSize = origin?.includes('localhost') ? 100 : 20000;
const MAX_NUM_OF_LOAN_OPEN_AT_ONCE = 10;
const isRowActionsMenuVisible = false;

const HbtPortfolioLoansInventoryTable = ({
  isCancellation,
  applicationID,
  portfolioIds,
  handleViewLoanDetailsActionRowClick,
  handleMutipleLoanDetailsActionClick,
  openFlyout,
  setOpenFlyout,
  openModifyRemarkFlyout,
  setModifyRemarkFlyout
}: HbtPortfolioLoansInventoryProps) => {
  const [loanInventoryError, setLoanInventoryError] = useState<string>('');
  const loansUpdateUrl: string =
    config.validationReviewInventoryApi.urls.loanInventoryTabDetails + `/loans/decision-overrides`;

  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [anchorElementActionsButton, setAnchorElementActionsButton] = useState<null | HTMLElement>(
    null
  );
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});
  const [selectedRowsData, setSelectedRowsData] = useState<LoanInventoryTableFields[]>([]);
  const [selectedEligibleRowsData, setSelectedEligibleRowsData] = useState<
    LoanInventoryTableFields[]
  >([]);

  const [selectedErrorCodes, setSelectedErrorCodes] = useState<Record<string, FilterOption[]>>({});
  const [selectedStatuses, setSelectedStatuses] = useState<FilterOption[]>([]);
  const [selectedPortfolios, setSelectedPortfolios] = useState<FilterOption[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);
  const [isSelectedFiltersMoreMenuOpen, setSelectedFiltersMoreMenuOpen] = useState(false);
  const [showRightPane, setShowRightPane] = useState(false);
  const handleCloseFlyout = () => setShowRightPane((prev) => !prev);
  const [showToaster, setShowToaster] = useState(false);
  const [openHistoricalFlyout, setOpenHistoricalFlyout] = React.useState<boolean>(false);
  const [isSelectionLimitModalOpen, setIsSelectionLimitModalOpen] = useState(false);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);

  const authenticationContext = useAuthenticationContext();
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { data: errorMeta } = useLoansErrors();
  const columns = useMemo(
    () => createLoansInventoryColumns(isCancellation, t, i18n.language),
    [isCancellation, t, i18n.language]
  );
  const errorMapping = errorSeverityMapping(t);
  const isViewLoanDetailsButtonVisible = !isCancellation && selectedRowsData.length > 0;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const location = useLocation();
  const { loanId } = getSearchParams(location.search);
  const areAllLoansSameStatus = !Boolean(
    selectedRowsData.find((data) => data.loanStatus !== selectedRowsData[0].loanStatus)
  );

  const translations = {
    searchList: t('Pi-Portfolio-Management-SearchList'),
    selectAll: t('Pi-Portfolio-Management-Select-All'),
    clearSection: t('Pi-Portfolio-Management-Clear-Selection'),
    selected: t('Pi-Portfolio-Management-Selected'),
    noInfo: t('Pi-Portfolio-Management-NoInfo')
  };

  const handleClick = (evt: React.MouseEvent<HTMLElement>, rowId: string) => {
    evt.stopPropagation();
    setAnchorElement(evt.currentTarget);
    setMenuRowId(rowId);
    setRowSelection({ [rowId]: true });
  };

  const handleClickActionsButton = (evt: React.MouseEvent<HTMLElement>) => {
    setAnchorElementActionsButton(evt.currentTarget);
  };

  const throttleRowFocus = useMemo(
    () =>
      lodash.throttle((row) => {
        const rowId = row.id;
        const newSelectedRow = { [rowId]: true };
        setRowSelection(newSelectedRow);
      }, 150),
    []
  );

  const handleCloseActionsButton = (event: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setAnchorElementActionsButton(null);
  };

  const [flyoutToOpen, setFlyoutToOpen] = useState<FlyoutType | undefined>(undefined);
  const handleOverrideLoanStatus = useCallback(
    (event: React.MouseEvent, type: FlyoutType) => {
      setFlyoutToOpen(type);
      const ineligibleLoans = getIneligibleLoans(selectedRowsData, isCancellation);
      const anyIneligibleLoans = ineligibleLoans.length > 0;
      if (!anyIneligibleLoans) {
        setSelectedEligibleRowsData(selectedRowsData);
      }
      updateModalTitle(type);
      setInEligibleModalOpen(anyIneligibleLoans);
      if (type === FlyoutType.modify) {
        setModifyRemarkFlyout(!anyIneligibleLoans);
      }
      if (type === FlyoutType.update) {
        setOpenFlyout(!anyIneligibleLoans);
      }
      setAnchorElementActionsButton(null);
      setAnchorElement(null);
      event?.stopPropagation();
    },
    [selectedRowsData]
  );

  const toggleRightPane = () => {
    setShowRightPane((prev) => !prev);
  };
  const handleRowClick = (
    row: MRT_Row<LoanInventoryTableFields>,
    _: number,
    evt: React.MouseEvent
  ) => {
    const isCheckBoxCol = Boolean((evt.target as HTMLElement).closest('.MuiCheckbox-root'));
    if (!isCheckBoxCol) {
      const rowId = row.id;
      const newSelectedRow = { [rowId]: true };
      setRowSelection(newSelectedRow);
    }
  };

  const handleClose = (event: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setAnchorElement(null);
    setMenuRowId(null);
  };

  const fetchInventoryData = async ({ pageParam = 0 }) => {
    const offset = pageParam;

    let sortField = sorting[0]?.id ?? '';

    if (sortField === 'approvedLender') {
      sortField = i18n.language === 'en' ? 'approvedLenderEnglishName' : 'approvedLenderFrenchName';
    }
    const sortOrder = !!sorting[0]?.desc;

    const queryParams = `?offset=${offset}&size=${fetchSize}${
      sortField ? `&sortField=${sortField}&sortOrder=${sortOrder ? 'desc' : 'asc'}` : ''
    }`;

    const data = await getLoanInventoryTableData({
      authenticationContext,
      applicationID,
      portfolioIds,
      queryParams
    });
    return data;
  };

  const {
    data: loanInventoryData,
    fetchNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
    hasNextPage,
    isError,
    isSuccess
  } = useInfiniteQuery({
    queryKey: ['portfolio-inventory-loans', selectedFilters, sorting, applicationID, portfolioIds],
    queryFn: fetchInventoryData,
    initialData: {
      pages: [[] as LoanInventoryTableFields[]],
      pageParams: [0]
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      const hasMore = lastPage.length === fetchSize;
      return hasMore ? totalFetched : undefined;
    }
  });

  const flatData = useMemo(() => {
    setRowSelection({});
    return loanInventoryData?.pages.flat() ?? [];
  }, [loanInventoryData]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  const paneConfig = {
    subHeaderText: isCancellation
      ? t('CMHC-Account-Number')
      : t('PI-Portfolio-Management-Loans-Details-Header-Reference-Number'),
    selectedRowSubHeaderData: isCancellation
      ? selectedRowsData[0]?.cmhcAccountNumber.toString()
      : selectedRowsData[0]?.lenderReferenceNumber,
    noErrorsContent: isCancellation
      ? t('PI-Portfolio-Management-Loans-Errors-Cmhc-SubBody')
      : t('PI-Portfolio-Management-Loans-No-Errors-SubBody')
  };

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    if (isError) {
      setLoanInventoryError('api_error');
    } else if (!isFetching && isSuccess && flatData.length > 0) {
      setLoanInventoryError('');
    } else if (!isFetching && isSuccess && flatData.length === 0) {
      setLoanInventoryError('no_data_error');
    }
  }, [isSuccess, flatData, loanInventoryError, isError, isFetching]);

  useEffect(() => {
    const updatedSelectedErrorCodes = { ...selectedErrorCodes };
    const orderedSelectedErrorCodes: Record<string, FilterOption[]> = {};

    Object.entries(errorMapping)
      .map(([severity]) => severity)
      .forEach((item) => {
        if (updatedSelectedErrorCodes[item]) {
          orderedSelectedErrorCodes[item] = updatedSelectedErrorCodes[item].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
          );
        }
      });

    setSelectedFilters([
      ...selectedStatuses,
      ...selectedPortfolios,
      ...Object.values(orderedSelectedErrorCodes).flat()
    ]);
  }, [selectedStatuses, selectedPortfolios, selectedErrorCodes]);

  const errorGrouped = useMemo(
    () => errorMeta && errorOptionsGrouped(flatData, errorMeta, t),
    [flatData, errorMeta]
  );

  const statusOptions = useMemo(() => statusOptionsGrouped(flatData, t), [flatData]);

  const portfolioOptions = useMemo(() => portfolioOptionsGrouped(flatData, t), [flatData]);

  const filteredData = useMemo(() => {
    if (selectedRowsData.length > 0 && selectedFilters.length > 0) {
      setRowSelection({});
      setSelectedRowsData([]);
      if (showRightPane) {
        handleCloseFlyout();
      }
    }
    return applyFilters(flatData, selectedErrorCodes, selectedStatuses, selectedPortfolios);
  }, [flatData, selectedErrorCodes, selectedStatuses, selectedPortfolios]);
  const mrtCustomTranslation = useMRTCustomTranslation(filteredData.length);
  let pptCode: string | null = null;
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  pptCode = searchParams.get('pptCode');
  const { isCancellationLoan, remarksDropDownHeader } = getRemarksDropDown(
    selectedRowsData,
    pptCode
  );

  useEffect(() => {
    const selectedRows = filteredData.filter((row, index) => rowSelection[index]);
    setSelectedRowsData(selectedRows);
  }, [rowSelection, filteredData]);

  const handleRemoveFilter = (filter: FilterOption) => {
    if (selectedStatuses.some((s) => s.value === filter.value)) {
      setSelectedStatuses((prev) => prev.filter((s) => s.value !== filter.value));
    }
    if (selectedPortfolios.some((p) => p.value === filter.value)) {
      setSelectedPortfolios((prev) => prev.filter((p) => p.value !== filter.value));
    }

    const [severity] = filter.value.split(':');
    setSelectedErrorCodes((prev) => ({
      ...prev,
      [severity]: (prev[severity] || []).filter((error) => error.value !== filter.value)
    }));
  };

  const handleClearAllFilters = () => {
    setSelectedStatuses([]);
    setSelectedPortfolios([]);
    setSelectedErrorCodes({});
  };

  const selectedFiltersMoreMenuOptions = useMemo(() => {
    return selectedFilters.slice(MAX_VISIBLE_FILTERS).map((filter, index) => ({
      label: filter.label,
      value: `${index}`,
      icon: <CmhcIcon size={CmhcIconSize.SMALL} type="icon_document" />,
      onClick: () => handleRemoveFilter(filter),
      chipLabel: filter.chipLabel
    }));
  }, [selectedFilters]);

  const toggleDrawer = (newOpen: boolean) => {
    setOpenFlyout(newOpen);
  };
  const toggleToaster = (error: boolean, message?: { header: string; subHeader: string }) => {
    if (error) {
      setToasterMsg({
        icon: 'icon_error_outlined',
        message: message || {
          header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Error'),
          subHeader: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Error-Desc')
        }
      });
    } else {
      setToasterMsg({
        icon: 'icon_check_circle',
        message: message || {
          header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
          subHeader: ' '
        }
      });
    }
    setShowToaster(true);
  };
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('PI-Portfolio-Overview-Flyout-Toaster-Header-Success'),
      subHeader: String(' ')
    }
  });

  const callToaster = (icon: string, message: string, desc = '') => {
    setToasterMsg({
      icon,
      message: {
        header: message,
        subHeader: desc
      }
    });

    setShowToaster(true);
  };

  const copyButtonText = {
    singular: t('Copy-Button'),
    plural: t('Copy-Button-Plural')
  };
  const copiedButtonText = {
    singular: t('Copied-Button'),
    plural: t('Copied-Button-Plural')
  };

  const InEligibleLoansModalBody = ({
    selectedLoans,
    desc,
    subDesc,
    primaryButton
  }: {
    selectedLoans: LoanInventoryTableFields[];
    desc: string;
    subDesc: string;
    primaryButton: string;
  }) => {
    const [hideSelectedLoans, setHideSelectedLoans] = useState(false);
    const [inEligibleLoans, setInEligibleLoans] = useState(
      getIneligibleLoans(selectedLoans, isCancellation)
    );
    const hideSelectedLoansButtonValue = hideSelectedLoans
      ? t('PI-Portfolio-Overview-Flyout-Hide-Ineligible-Loans')
      : t('PI-Portfolio-Overview-Flyout-Show-Ineligible-Loans');

    const removeItem = (loan: LoanInventoryTableFields) => {
      const newLoans = inEligibleLoans.filter((item) => item.id !== loan.id);
      setInEligibleLoans(newLoans);
    };

    return (
      <Stack>
        <Box>
          <RichTextBody translationKey={desc} />

          {getEligibleLoans().length > 0 && inEligibleLoans.length > 0 && (
            <LoansDropDown
              buttonValue={`${hideSelectedLoansButtonValue}`}
              buttonAriaText={`${hideSelectedLoansButtonValue}`}
              showLoans={hideSelectedLoans}
              setShowLoans={setHideSelectedLoans}
              loans={inEligibleLoans}
              removeLoanFn={removeItem}
            />
          )}
          {getEligibleLoans()?.length > 0 && <Typography>{subDesc}</Typography>}
        </Box>

        {getEligibleLoans().length > 0 && inEligibleLoans.length > 0 && (
          <Box className={styles.InEligibleLoansModalButton}>
            <Button
              type="button"
              name={`btn`}
              onClick={closeInEligibleModal}
              variant={ButtonVariant.SECONDARY}
              text={{
                value: t('PI-Portfolio-Overview-Flyout-Modal-Seconday-Button')
              }}
              ariaText={{
                value: t('PI-Portfolio-Overview-Flyout-Modal-Seconday-Button')
              }}
            />
            <Button
              type="button"
              name={`btn`}
              onClick={openFlyoutWithEligibleLoans}
              variant={ButtonVariant.PRIMARY}
              text={{
                value: primaryButton
              }}
              ariaText={{
                value: primaryButton
              }}
            />
          </Box>
        )}
      </Stack>
    );
  };

  const [isInEligibleModalOpen, setInEligibleModalOpen] = useState(false);
  const openInEligibleModal = () => {
    setInEligibleModalOpen(true);
  };
  const closeInEligibleModal = () => {
    setInEligibleModalOpen(false);
  };
  const openFlyoutWithEligibleLoans = () => {
    setSelectedEligibleRowsData(getEligibleLoans());
    closeInEligibleModal();
    if (flyoutToOpen === FlyoutType.modify) {
      setModifyRemarkFlyout(true);
    }
    if (flyoutToOpen === FlyoutType.update) {
      setOpenFlyout(true);
    }
  };
  const getEligibleLoans = () => {
    const ineligibleLoans = getIneligibleLoans(selectedRowsData, isCancellation);
    return selectedRowsData.filter((loan) => !ineligibleLoans.includes(loan));
  };

  const [modalTitle, setModalTitle] = useState('');
  const updateModalTitle = useCallback(
    (flyoutType: FlyoutType) => {
      if (selectedRowsData.length === 1) {
        const title =
          flyoutType === FlyoutType.modify
            ? t('PI-Portfolio-Overview-Flyout-Modal-Title-Cannot-Add-Remarks')
            : t('PI-Portfolio-Overview-Flyout-Modal-Title');
        setModalTitle(title);
        return undefined;
      }

      const noOfIneligibleLoans = getIneligibleLoans(selectedRowsData, isCancellation).length;
      const allLoansAreIneligible = noOfIneligibleLoans === selectedRowsData.length;

      if (allLoansAreIneligible) {
        const title =
          flyoutType === FlyoutType.modify
            ? t('PI-Portfolio-Overview-Flyout-Modal-Modify-Remark-Title-Multiple-InEligibleLoans')
            : t('PI-Portfolio-Overview-Flyout-Modal-Title-Multiple-none');
        setModalTitle(title);
        return undefined;
      }

      const title =
        flyoutType === FlyoutType.modify
          ? t('PI-Portfolio-Overview-Flyout-Modal-Modify-Remark-Title-InEligibleLoans-Some')
          : t('PI-Portfolio-Overview-Flyout-Modal-Title-Multiple');
      setModalTitle(title);
      return undefined;
    },
    [selectedRowsData, t]
  );

  const leftColumn = isCancellation ? 'cmhcAccountNumber' : 'lenderReferenceNumber';
  const showTableData = !isLoading && !isFetching && isSuccess && loanInventoryError.length < 1;
  const showErrorComponent = !isLoading && !isFetching && loanInventoryError.length > 0;

  useEffect(() => {
    if (
      loanId.length > 0 &&
      filteredData.length > 0 &&
      sessionStorage.getItem('searchLoanDetail')
    ) {
      const selectedRowsData = filteredData.filter((row) => row.id === loanId);
      selectedRowsData.map((row) => {
        const tabField: TabFields = {
          label: { value: row.lenderReferenceNumber },
          content: (
            <HbtPortfolioIndividualLoanDetails
              portfolioId={row.portfolioId}
              loanReferenceNumber={row.lenderReferenceNumber}
              loansId={row.id}
              latestRemarks={row.latestRemark}
              loanPortfolioEpipProcessingStatus={row.portfolioEpipProcessingStatus}
              loanInventoryData={row}
              toggleToaster={toggleToaster}
            />
          )
        };
        handleViewLoanDetailsActionRowClick(tabField);
      });
    }
  }, [loanId, filteredData]);

  const selectedRowsWithHistoricalData = selectedRowsData.filter(
    (row) => row.remarks && row.remarks?.length > 0
  );

  const handleHistoricalFlyout = () => {
    if (selectedRowsWithHistoricalData.length > MAX_NUM_OF_LOAN_OPEN_AT_ONCE) {
      setIsSelectionLimitModalOpen(true);
      setAnchorElementActionsButton(null);
      return;
    }

    if (!openHistoricalFlyout) {
      setOpenHistoricalFlyout(true);
    }
    setAnchorElementActionsButton(null);
  };

  const rowsDataWithHistoricalData = (data: LoanInventoryTableFields[]) => {
    const total = data.filter((row) => row.remarks && row.remarks?.length > 0);
    return total.length;
  };

  const handleHistoricalDataFlyout = () => {
    setOpenHistoricalFlyout(false);
    setSelectedRowsData([]);
    setRowSelection({});
  };
  return (
    <>
      <Modal
        id={styles.inEligibleLoansModal}
        isActive={isInEligibleModalOpen}
        onDismissalCallback={closeInEligibleModal}
        title={{ value: modalTitle }}
        bodyContent={
          <InEligibleLoansModalBody
            selectedLoans={selectedRowsData}
            desc={
              flyoutToOpen === FlyoutType.update
                ? t('PI-Portfolio-Overview-Flyout-Modal-Title-Multiple-body-1')
                : isCancellation
                  ? t('PI-Portfolio-Overview-Flyout-Modal-Body-Cancellation')
                  : t('PI-Portfolio-Overview-Flyout-Modal-Body-Non-Cancellation')
            }
            subDesc={t('PI-Portfolio-Overview-Flyout-Modal-subDesc')}
            primaryButton={
              flyoutToOpen === FlyoutType.update
                ? t('PI-Portfolio-Overview-Flyout-Modal-Primary-Button')
                : t('PI-Portfolio-Overview-Flyout-Modal-Modify-Remark-Primary-Button')
            }
          />
        }
        shouldCloseOnBackgroundClick={true}
      />
      <Modal
        id={'selction-limit-reached'}
        isActive={isSelectionLimitModalOpen}
        onDismissalCallback={() => {
          setIsSelectionLimitModalOpen(false);
        }}
        title={{ value: t('Selection-Limit-Reached') }}
        bodyContent={t('Selection-Limit-Body-Text-Historical-Data-For-CMHC-Account-Numbers')}
        shouldCloseOnBackgroundClick={true}
      />

      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>
      <ForceStatusFlyout
        openFlyout={openFlyout}
        toggleDrawer={toggleDrawer}
        loans={selectedEligibleRowsData
          .map((rows) => (isCancellationLoan ? rows.cmhcAccountNumber : rows.lenderReferenceNumber))
          .join('\n')}
        loansData={selectedEligibleRowsData}
        radioButtonOptions={[
          {
            label: `${t('Passed')}`,
            value: `${LoanStatus.pass}`
          },
          {
            label: `${t('Failed')}`,
            value: `${LoanStatus.fail}`
          }
        ]}
        requestUrl={loansUpdateUrl}
        typeOfFlyout="updateLoan"
        headerText={
          isCancellationLoan
            ? t('Update-Loan-Header-Text-For-Cancellations')
            : t('Update-Loan-Header-Text')
        }
        loansToBeUpdatedTitle={
          isCancellationLoan ? undefined : t('Update-Loan-Loans-To-Be-Updated')
        }
        loansTypeToBeAdded={isCancellationLoan ? undefined : t('Update-Loan-To-Be-Added')}
        textFieldPlaceholder={isCancellationLoan ? undefined : t('Add-Loan-Textbox-Placeholder')}
        textFieldButtonText={
          isCancellationLoan
            ? t('Add-CMHC-Account-Numbers')
            : t('Update-Loan-Text-Field-Button-Text')
        }
        addLoansDescription={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Description')
        }
        addLoansTextFieldError={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error')
        }
        loansUpdateLargeFormFieldButtonText={
          isCancellationLoan ? undefined : t('Validate-Reference-Number')
        }
        toasterHeader={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Header')}
        toasterDesc={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Description')}
        addLoansTextFieldErrorTwo={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error-Two')
        }
        remarksTextField={isCancellationLoan ? undefined : t('Update-Loan-Remark-Text-Field')}
        successFlyoutTitle={isCancellationLoan ? undefined : t('Confirmation-Flyout-Lender')}
        copyButtonText={isCancellationLoan ? undefined : copyButtonText}
        copiedButtonText={isCancellationLoan ? undefined : copiedButtonText}
        overrideModalDesc={isCancellationLoan ? undefined : t('Override-Modal-Desc')}
        errorModalDesc={
          isCancellationLoan ? t('Error-Modal-Desc-Status-Cancellation') : t('Error-Modal-Desc')
        }
        typeOfPortfolioInsurance={'validationReview'}
        successFlyoutDesc={isCancellationLoan ? undefined : ' '}
        callToaster={callToaster}
      />
      <ForceStatusFlyout
        callToaster={callToaster}
        openFlyout={openModifyRemarkFlyout}
        toggleDrawer={setModifyRemarkFlyout}
        loans={selectedEligibleRowsData
          .map((rows) => (isCancellationLoan ? rows.cmhcAccountNumber : rows.lenderReferenceNumber))
          .join('\n')}
        loansData={selectedEligibleRowsData}
        remarksDropDownHeader={remarksDropDownHeader}
        requestUrl={loansUpdateUrl}
        typeOfFlyout="updateRemark"
        flyoutTitle={t('Add-Remarks')}
        typeOfPortfolioInsurance={'validationReview'}
        headerText={t('Update-Remark-Header-Text')}
        loansToBeUpdatedTitle={
          isCancellationLoan ? undefined : t('Update-Loan-Loans-To-Be-Updated')
        }
        loansUpdateLargeFormFieldButtonText={
          isCancellationLoan ? undefined : t('Validate-Reference-Number')
        }
        loansTypeToBeAdded={isCancellationLoan ? undefined : t('Update-Loan-To-Be-Added')}
        textFieldPlaceholder={isCancellationLoan ? undefined : t('Add-Loan-Textbox-Placeholder')}
        textFieldButtonText={
          isCancellationLoan
            ? t('Add-CMHC-Account-Numbers')
            : t('Update-Loan-Text-Field-Button-Text')
        }
        addLoansDescription={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Description')
        }
        addLoansTextFieldError={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error')
        }
        toasterHeader={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Header')}
        toasterDesc={isCancellationLoan ? undefined : t('Update-Loan-Toaster-Description')}
        addLoansTextFieldErrorTwo={
          isCancellationLoan ? undefined : t('Update-Loan-Add-Loans-Text-Field-Error-Two')
        }
        remarksTextField={isCancellationLoan ? undefined : t('Update-Loan-Remark-Text-Field')}
        successFlyoutTitle={isCancellationLoan ? undefined : t('Confirmation-Flyout-Lender')}
        copyButtonText={isCancellationLoan ? undefined : copyButtonText}
        copiedButtonText={isCancellationLoan ? undefined : copiedButtonText}
        overrideModalDesc={isCancellationLoan ? undefined : t('Override-Modal-Desc')}
        errorModalDesc={
          isCancellationLoan
            ? t('Error-Modal-Desc-Remarks-Cancellation')
            : t('Error-Modal-Desc-Remarks-Non-Cancellation')
        }
        errorModalProceedText={
          isCancellationLoan
            ? t('Error-Modal-Proceed-Text-Cancellation')
            : t('Error-Modal-Proceed-Text-Non-Cancellation')
        }
        successFlyoutDesc={isCancellationLoan ? undefined : ' '}
        errorModalHeader={t('Error-Modal-Header')}
      />
      {isFetching && (
        <Box className={styles.spinnerElementWrapper}>
          <SpinnerElement />
        </Box>
      )}
      {showErrorComponent && (
        <Box
          sx={{
            padding: '32px;'
          }}
        >
          <ErrorMessage errorType={loanInventoryError} />
        </Box>
      )}

      <HistoricalDataFlyout
        openFlyout={openHistoricalFlyout}
        toggleFlyout={handleHistoricalDataFlyout}
        selectedRowsData={selectedRowsData}
        isItMasterPoolLoans={false}
      />
      {showTableData && (
        <>
          <Box className={styles.actionDetailsButtonsWrapper}>
            <Button
              type="button"
              name="Override-Status"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              leadingIcon={() => (
                <CmhcIcon
                  size={CmhcIconSize.SMALL}
                  type="icon_override"
                  className={styles.overrideButton}
                />
              )}
              text={{
                value: `${t('Override-Status')} ${selectedRowsData.length ? `(${selectedRowsData.length})` : ''}`
              }}
              ariaText={{
                value: `${t('Override-Status')} ${selectedRowsData.length ? `(${selectedRowsData.length})` : ''})`
              }}
              className={styles.button}
              onClick={(evt: React.MouseEvent) => {
                handleOverrideLoanStatus(evt, FlyoutType.update);
              }}
            />
            <Button
              type="button"
              name="details"
              size={ButtonSize.SMALL}
              variant={ButtonVariant.TERTIARY}
              leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_details" />}
              text={{ value: t('PI-Portfolio-Management-Request-Overview-Details') }}
              ariaText={{
                value: t('PI-Portfolio-Management-Request-Overview-Details')
              }}
              className={styles.button}
              onClick={toggleRightPane}
            />
            {selectedRowsData.length >= 1 && (
              <Button
                type="button"
                onClick={handleClickActionsButton}
                name="actions"
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />}
                text={{ value: t('PI-Portfolio-Management-Request-Overview-Actions') }}
                ariaText={{
                  value: t('PI-Portfolio-Management-Request-Overview-Actions')
                }}
                className={styles.button}
              />
            )}
            {isViewLoanDetailsButtonVisible && (
              <Button
                type="button"
                name="details"
                size={ButtonSize.SMALL}
                onClick={() => {
                  const tabs = selectedRowsData.map((row) => {
                    const tabField: TabFields = {
                      label: { value: row.lenderReferenceNumber },
                      content: (
                        <HbtPortfolioIndividualLoanDetails
                          portfolioId={row.portfolioId}
                          loanReferenceNumber={row.lenderReferenceNumber}
                          loansId={row.id}
                          latestRemarks={row.latestRemark}
                          loanPortfolioEpipProcessingStatus={row.portfolioEpipProcessingStatus}
                          loanInventoryData={row}
                          toggleToaster={toggleToaster}
                        />
                      )
                    };
                    return tabField;
                  });
                  handleMutipleLoanDetailsActionClick(tabs);
                }}
                variant={ButtonVariant.PRIMARY}
                text={{ value: `${t('View-Loan-Details')} (${selectedRowsData.length})` }}
                ariaText={{
                  value: t('PI-Portfolio-Management-Request-Overview-Details')
                }}
                className={styles.viewLoanDetailsButton}
              />
            )}
          </Box>

          <Box className={styles.filterDropdownWrapper}>
            <Box className={styles.quickFiltersTitle}>
              {t('Pi-Portfolio-Management-Quick-Filters')}{' '}
            </Box>
            <DropdownWithSearch
              label={t('Pi-Portfolio-Management-Loan-Status')}
              options={statusOptions}
              selected={selectedStatuses}
              setSelected={setSelectedStatuses}
              translations={translations}
            />
            <DropdownWithSearch
              label={t('Pi-Portfolio-Management-PortfolioId')}
              options={portfolioOptions}
              selected={selectedPortfolios}
              setSelected={setSelectedPortfolios}
              translations={translations}
            />
            {errorGrouped &&
              Object.entries(errorGrouped).map(([severity, options], index) => (
                <DropdownWithSearch
                  key={index}
                  label={errorMapping[severity as keyof typeof errorMapping]}
                  options={options as FilterOption[]}
                  translations={translations}
                  selected={selectedErrorCodes[severity] ?? []}
                  setSelected={(newSelection: any) =>
                    setSelectedErrorCodes((prev) => ({
                      ...prev,
                      [severity]: newSelection
                    }))
                  }
                />
              ))}
          </Box>
          {selectedFilters.length > 0 && (
            <Box className={styles.filterChips}>
              {selectedFilters.slice(0, MAX_VISIBLE_FILTERS).map((filter) => (
                <Tag
                  key={filter.value}
                  label={filter.chipLabel || ''}
                  tagType={TagType.TAG}
                  tagVariant={TagVariant.INTERACTIVE}
                  onDelete={() => handleRemoveFilter(filter)}
                  showStatusIndicator={false}
                />
              ))}
              {selectedFilters.length > MAX_VISIBLE_FILTERS && (
                <ClickAwayListener onClickAway={() => setSelectedFiltersMoreMenuOpen(false)}>
                  <Box>
                    <ActionDropdownWithListItems
                      className={styles.selectedFiltersMoreMenu}
                      open={isSelectedFiltersMoreMenuOpen}
                      onToggle={setSelectedFiltersMoreMenuOpen}
                      options={selectedFiltersMoreMenuOptions}
                      trigger={
                        <Button
                          variant={ButtonVariant.TERTIARY}
                          name="more-options-dropdown-button"
                          text={{
                            value: `+ ${selectedFilters.length - MAX_VISIBLE_FILTERS} ${t(
                              'Pi-Portfolio-Management-Quick-Filters-More'
                            )}`
                          }}
                        />
                      }
                    />
                  </Box>
                </ClickAwayListener>
              )}
              <Button
                onClick={handleClearAllFilters}
                variant={ButtonVariant.TERTIARY}
                name="Clear Filters"
                text={{
                  value: t('Pi-Portfolio-Management-Clear-Filters')
                }}
                leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_clear_circle" />}
              >
                {t('Pi-Portfolio-Management-Clear-Filters')}
              </Button>
            </Box>
          )}

          <Menu
            sx={{
              minWidth: '250px',
              '.MuiMenuItem-root': {
                minHeight: '50px'
              },
              '.MuiMenuItem-root:Hover': {
                backgroundColor: '#EDECFA'
              },
              '.MuiPaper-root': {
                boxShadow: 'unset',
                border: '2px solid #697885',
                width: 'fit-content'
              }
            }}
            open={Boolean(anchorElementActionsButton)}
            anchorEl={anchorElementActionsButton}
            onClose={handleCloseActionsButton}
            className={styles.popupMenuContainer}
          >
            <>
              {selectedRowsWithHistoricalData.length > 0 && (
                <MenuItem onClick={handleHistoricalFlyout} className={styles.popupMenu}>
                  <CmhcIcon size={CmhcIconSize.SMALL} type="icon_history" />
                  <span>
                    {t('View-Historical-Remark')} (
                    {t('Count-Available', {
                      count: Number(rowsDataWithHistoricalData(selectedRowsData))
                    })}
                    )
                  </span>
                </MenuItem>
              )}
              <MenuItem
                onClick={(evt: React.MouseEvent) => {
                  handleOverrideLoanStatus(evt, FlyoutType.modify);
                }}
                className={styles.popupMenu}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_comment_add" />
                <span>
                  {t('Add-Remarks')} ({t('Selected', { count: selectedRowsData.length })})
                </span>
              </MenuItem>
            </>
          </Menu>
          <SplitPane
            leftPaneMinWidth={500}
            rightPaneMinWidth={374}
            showRightPane={showRightPane}
            leftPane={
              <div className={styles.dataTableContainer}>
                <MRTDataTable<LoanInventoryTableFields>
                  data={filteredData || []}
                  columns={columns}
                  localization={mrtCustomTranslation}
                  enableRowVirtualization={true}
                  enableColumnVirtualization={true}
                  enableRowSelection
                  memoMode="cells"
                  columnResizeMode="onChange"
                  muiTableBodyCellProps={({ row }) => ({
                    tabIndex: 0,
                    onFocus: (e) => {
                      if ((e.target as HTMLElement).matches(':focus-visible') && showRightPane) {
                        throttleRowFocus(row);
                      }
                    }
                  })}
                  enableRowActions={isRowActionsMenuVisible}
                  enableSorting={true}
                  isLoading={isLoading}
                  initialState={{
                    columnPinning: {
                      left: isRowActionsMenuVisible
                        ? ['mrt-row-actions', 'mrt-row-select', leftColumn]
                        : ['mrt-row-select', leftColumn]
                    }
                  }}
                  enablePagination={false}
                  enableBottomToolbar={false}
                  onRowClick={handleRowClick}
                  onRowSelectionChange={setRowSelection}
                  muiTableBodyRowProps={({ row }) => ({
                    tabIndex: 0,
                    onClick: (e) => handleRowClick(row, 0, e),
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') {
                        row.toggleSelected();
                      }
                    }
                  })}
                  enableStickyHeader={true}
                  enableSortingRemoval={false}
                  renderRowActions={({ row }) => (
                    <>
                      <Box
                        aria-label={`row-action-${row.id}`}
                        className={styles.loanInventoryActionButton}
                        onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) =>
                          handleClick(e, row.id)
                        }
                      >
                        <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />
                      </Box>

                      <Menu
                        sx={{
                          minWidth: '250px',
                          '.MuiMenuItem-root': {
                            minHeight: '50px'
                          },
                          '.MuiMenuItem-root:Hover': {
                            backgroundColor: '#EDECFA'
                          },
                          '.MuiPaper-root': {
                            boxShadow: 'unset',
                            border: '2px solid #697885',
                            width: 'fit-content'
                          }
                        }}
                        open={Boolean(anchorElement) && menuRowId === row.id}
                        anchorEl={anchorElement}
                        onClose={handleClose}
                      >
                        <Box className={styles.popupMenuContainer}>
                          {!isCancellation && (
                            <MenuItem
                              onClick={(evt: React.MouseEvent) => {
                                selectedRowsData.map((row) => {
                                  const tabField: TabFields = {
                                    label: { value: row.lenderReferenceNumber },
                                    content: (
                                      <HbtPortfolioIndividualLoanDetails
                                        portfolioId={row.portfolioId}
                                        loanReferenceNumber={row.lenderReferenceNumber}
                                        loansId={row.id}
                                        latestRemarks={row.latestRemark}
                                        loanPortfolioEpipProcessingStatus={
                                          row.portfolioEpipProcessingStatus
                                        }
                                        loanInventoryData={row}
                                        toggleToaster={toggleToaster}
                                      />
                                    )
                                  };
                                  handleViewLoanDetailsActionRowClick(tabField);
                                });
                                handleClose(evt);
                              }}
                              className={styles.popupMenu}
                            >
                              <CmhcIcon
                                size={CmhcIconSize.SMALL}
                                className={styles.icon_more}
                                type="icon_read_more"
                              />
                              <span>{t('View-Loan-Details')}</span>
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={(evt: React.MouseEvent) => {
                              handleOverrideLoanStatus(evt, FlyoutType.update);
                            }}
                            className={styles.popupMenu}
                          >
                            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_override" />
                            <span>{t('Override-Status')}</span>
                          </MenuItem>
                          <MenuItem
                            onClick={(evt: React.MouseEvent) => {
                              handleOverrideLoanStatus(evt, FlyoutType.modify);
                            }}
                            className={styles.popupMenu}
                          >
                            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_comment_add" />
                            <span>{t('Add-Remarks')}</span>
                          </MenuItem>
                          {row.original.remarks && row.original.remarks?.length > 0 && (
                            <MenuItem
                              onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                                setOpenHistoricalFlyout(true);
                                handleClose(event);
                              }}
                              className={styles.popupMenu}
                            >
                              <CmhcIcon size={CmhcIconSize.SMALL} type="icon_history" />
                              <span>{t('View-Historical-Data')}</span>
                            </MenuItem>
                          )}
                        </Box>
                      </Menu>
                    </>
                  )}
                  muiTableContainerProps={{
                    ref: tableContainerRef,
                    sx: { maxHeight: '600px', minHeight: '300px' },
                    onScroll: (event: UIEvent<HTMLDivElement>) =>
                      fetchMoreOnBottomReached(event.target as HTMLDivElement)
                  }}
                  manualSorting
                  onSortingChange={setSorting}
                  state={{
                    rowSelection,
                    isLoading: isLoading || isFetching,
                    sorting
                  }}
                />
              </div>
            }
            rightPane={
              <RightPaneContent
                subHeaderText={paneConfig.subHeaderText}
                bodyContent={t('PI-Portfolio-Management-Loans-Details-SubHeader')}
                selectedRowSubHeaderData={paneConfig.selectedRowSubHeaderData}
                noResultsBodyContent={t('PI-Portfolio-Management-Loans-No-Errors-Loan')}
                moreThenOneIsSelectedContent={t(
                  'PI-Portfolio-Management-Loans-Details-More-Than-One-Selected'
                )}
                handleCloseFlyout={handleCloseFlyout}
                noErrorsContent={paneConfig.noErrorsContent}
                count={selectedRowsData.length}
                data={selectedRowsData}
              />
            }
          />
        </>
      )}
    </>
  );
};

export default memo(HbtPortfolioLoansInventoryTable);

function getIneligibleLoans(selectedRowsData: LoanInventoryTableFields[], isCancellation: boolean) {
  return selectedRowsData.filter((loan) => {
    if (isCancellation) {
      return (
        loan.portfolioEpipProcessingStatus !== EpipProcessingStatusCode.VALIDATION_COMPLETED &&
        loan.portfolioEpipProcessingStatus !== EpipProcessingStatusCode.CANCELLATION_COMPLETED
      );
    }
    return (
      loan.portfolioEpipProcessingStatus !== EpipProcessingStatusCode.VALIDATION_COMPLETED &&
      loan.portfolioEpipProcessingStatus !== EpipProcessingStatusCode.SENT_TO_EPIP
    );
  });
}
